import React, { useEffect, useState } from 'react';
import { ChevronDown, ChevronUp, HelpCircle } from 'lucide-react';
import { Helmet } from 'react-helmet-async';
import api from '../api/client';

/**
 * Renders a FAQ accordion on Home / Contact pages and injects the FAQPage
 * JSON-LD schema so Google can pick the answers up as rich results.
 *
 *   <FAQSection slug="home" heading="Frequently asked questions" />
 */
const FAQSection = ({ slug, heading = 'Frequently asked questions', subheading }) => {
  const [items, setItems] = useState([]);
  const [openIdx, setOpenIdx] = useState(0);

  useEffect(() => {
    if (!slug) return;
    api.get(`/faqs/${slug}`)
      .then(r => setItems(r.data?.items || []))
      .catch(() => setItems([]));
  }, [slug]);

  if (!items.length) return null;

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: items.map(item => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: { '@type': 'Answer', text: item.answer },
    })),
  };

  return (
    <section className="max-w-4xl mx-auto px-4 py-10" data-testid={`faq-section-${slug}`}>
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>
      <div className="mb-6">
        <div className="flex items-center gap-2 text-[#F46A25] mb-1">
          <HelpCircle className="w-5 h-5" />
          <span className="text-xs uppercase tracking-widest font-bold">FAQ</span>
        </div>
        <h2 className="text-2xl md:text-3xl font-black text-[#12263A]">{heading}</h2>
        {subheading && <p className="text-sm text-neutral-600 mt-1">{subheading}</p>}
      </div>
      <ul className="space-y-2">
        {items.map((it, i) => (
          <li key={i} className="bg-white border border-neutral-200 rounded-sm" data-testid={`faq-item-${slug}-${i}`}>
            <button
              type="button"
              onClick={() => setOpenIdx(openIdx === i ? -1 : i)}
              aria-expanded={openIdx === i}
              className="w-full flex items-start justify-between gap-3 text-left px-4 py-3 hover:bg-orange-50"
              data-testid={`faq-toggle-${slug}-${i}`}
            >
              <span className="font-bold text-[#12263A] text-sm md:text-base">{it.question}</span>
              {openIdx === i ? <ChevronUp className="w-4 h-4 shrink-0 mt-0.5 text-[#F46A25]" />
                             : <ChevronDown className="w-4 h-4 shrink-0 mt-0.5 text-neutral-400" />}
            </button>
            {openIdx === i && (
              <div className="px-4 pb-4 text-sm text-neutral-700 leading-relaxed" data-testid={`faq-answer-${slug}-${i}`}>
                {it.answer}
              </div>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
};

export default FAQSection;
